import * as constants from '../../../common/constant';
import { events } from '../../../common/constant';
import { findCellById, getAdjacentBlock, getAdjacentCell, getBlockContentElement, getBlockModelById, getDataCell, getDeepestTextNode, getSelectedCells, hasActiveTableSelection, isAtEndOfBlock, isAtStartOfBlock, setCursorPosition, toDomCol } from '../../../common/utils/index';
export class TableSelectionManager {
    constructor(parent) {
        // rectangle selection anchors
        this.multiselectStartRow = null;
        this.multiselectEndRow = null;
        this.multiselectStartCol = null;
        this.multiselectEndCol = null;
        this.parent = parent;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.observer.on('keydown', this.handleKeyDown, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListener() {
        this.parent.observer.off('keydown', this.handleKeyDown);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    attachTableEvents(table, blockElement) {
        let isMouseSelecting = false;
        let dragStartCell = null;
        let selectionRectActive = false;
        const onMouseDown = (e) => {
            const target = e.target;
            const cell = target.closest('td, th');
            if (!cell || cell.classList.contains('e-row-number')) {
                return;
            }
            isMouseSelecting = true;
            dragStartCell = cell;
            selectionRectActive = false;
            const startRow = parseInt(cell.dataset.row, 10);
            const startCol = parseInt(cell.dataset.col, 10);
            this.multiselectStartRow = startRow;
            this.multiselectStartCol = startCol;
            this.multiselectEndRow = startRow;
            this.multiselectEndCol = startCol;
        };
        const onMouseMove = (e) => {
            if (!isMouseSelecting) {
                return;
            }
            const target = e.target;
            const cell = target.closest('td, th');
            if (!cell || cell.classList.contains('e-row-number')) {
                return;
            }
            if (!selectionRectActive && dragStartCell && cell !== dragStartCell) {
                selectionRectActive = true;
                blockElement.style.userSelect = 'none';
                table.style.userSelect = 'none';
            }
            if (!selectionRectActive) {
                return;
            }
            const endRow = parseInt(cell.dataset.row, 10);
            const endCol = parseInt(cell.dataset.col, 10);
            // to avoid popup flicker
            if (endRow === this.multiselectEndRow && endCol === this.multiselectEndCol) {
                return;
            }
            this.updateRectangleFocus(this.multiselectStartRow, this.multiselectStartCol, endRow, endCol, table);
            this.multiselectEndRow = endRow;
            this.multiselectEndCol = endCol;
            // Auto-scroll while dragging selection to keep current cell in view
            this.ensureCellVisible(table, cell);
            // Clear any native selection range while dragging across cells
            this.clearRangeAndSetCursor(table);
            const blockId = table.getAttribute('data-block-id');
            if (blockId) {
                const uiManager = this.parent.blockRenderer.tableRenderer.getManager(blockId);
                if (uiManager) {
                    uiManager.hideRowGripper();
                    uiManager.hideAllPinnedColBars();
                    const minRow = Math.min(this.multiselectStartRow, this.multiselectEndRow);
                    const maxRow = Math.max(this.multiselectStartRow, this.multiselectEndRow);
                    const minCol = Math.min(this.multiselectStartCol, this.multiselectEndCol);
                    const maxCol = Math.max(this.multiselectStartCol, this.multiselectEndCol);
                    const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
                    const settings = block.properties;
                    const totalRows = settings.rows.length + (settings.enableHeader ? 1 : 0);
                    const totalCols = settings.columns.length;
                    const isFullRowSelection = minCol === 0 && maxCol >= totalCols - 1;
                    uiManager.removeRowColSelection(table);
                    if (isFullRowSelection) {
                        let first = true;
                        for (let r = minRow; r <= maxRow; r++) {
                            uiManager.showRowGripperForDomRow(r, first);
                            first = false;
                        }
                    }
                    else {
                        uiManager.hideRowGripper();
                        if (uiManager.popupObj) {
                            uiManager.handleRemovePopup();
                        }
                    }
                    const isFullColumnSelection = minRow === 0 && maxRow >= totalRows - 1;
                    if (isFullColumnSelection) {
                        let first = true;
                        for (let c = minCol; c <= maxCol; c++) {
                            uiManager.showColGripperForDomCol(c, first);
                            first = false;
                        }
                    }
                    else {
                        uiManager.hideAllPinnedColBars();
                    }
                }
            }
            e.preventDefault();
            e.stopPropagation();
        };
        const onMouseUp = () => {
            isMouseSelecting = false;
            dragStartCell = null;
            selectionRectActive = false;
            // Only clear multi-select if no selection rectangle is active (i.e., mouseup without drag)
            // If a selection rectangle is active, keep the multi-select state for shift+arrow
            if (this.multiselectStartRow === this.multiselectEndRow && this.multiselectStartCol === this.multiselectEndCol) {
                this.multiselectStartRow = null;
                this.multiselectStartCol = null;
                this.multiselectEndRow = null;
                this.multiselectEndCol = null;
            }
            blockElement.style.userSelect = '';
            table.style.userSelect = '';
        };
        table.addEventListener('mousedown', onMouseDown);
        table.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }
    // Ensure the given cell is visible inside the scrollable table container
    ensureCellVisible(table, cell) {
        const container = table.parentElement;
        const margin = 12; // small breathing room
        const cRect = container.getBoundingClientRect();
        const cellRect = cell.getBoundingClientRect();
        // Horizontal scroll
        if (cellRect.left < cRect.left) {
            const dx = cRect.left - cellRect.left + margin;
            container.scrollLeft -= dx;
        }
        else if (cellRect.right > cRect.right) {
            const dx = cellRect.right - cRect.right + margin;
            container.scrollLeft += dx;
        }
    }
    getAllCellBlocks(tableBlock) {
        const tableBlockModel = getBlockModelById(tableBlock.id, this.parent.getEditorBlocks());
        const allBlocks = [];
        tableBlockModel.properties.rows.forEach((row) => {
            row.cells.forEach((cell) => {
                allBlocks.push(...cell.blocks);
            });
        });
        return allBlocks;
    }
    getSelectedCellBlocks(tableBlock) {
        const tableBlockModel = getBlockModelById(tableBlock.id, this.parent.getEditorBlocks());
        const selectedCells = getSelectedCells(tableBlock);
        const selectedBlocks = [];
        selectedCells.forEach((cell) => {
            if (cell.tagName === 'TH') {
                return;
            }
            const cellId = cell.querySelector(`.${constants.TABLE_CELL_BLK_CONTAINER}`).id;
            const cellModel = findCellById(cellId, [tableBlockModel]);
            selectedBlocks.push(...cellModel.blocks);
        });
        return selectedBlocks;
    }
    isCaretAtHeaderBoundary(th, edge) {
        const sel = this.parent.nodeSelection.getSelection();
        if (!sel || sel.rangeCount === 0) {
            return true;
        }
        const range = sel.getRangeAt(0);
        if (!th.contains(range.startContainer) || !th.contains(range.endContainer)) {
            return true;
        }
        // Compute start: at beginning of th
        if (edge === 'start') {
            return range.collapsed && range.startOffset === 0;
        }
        // Compute end: at end of th
        // Try to use endContainer length
        const endNodeText = (range.endContainer && (range.endContainer.textContent || '')) || '';
        if (range.endOffset === endNodeText.length && th.contains(range.endContainer)) {
            return true;
        }
        const thTextLen = (th.textContent || '').length;
        return thTextLen === 0 || range.toString().length === 0;
    }
    shouldMoveHorizFromHeader(direction, headerCell) {
        return direction === 'left'
            ? this.isCaretAtHeaderBoundary(headerCell, 'start')
            : this.isCaretAtHeaderBoundary(headerCell, 'end');
    }
    handleKeyDown(e) {
        const targetEl = e.target;
        const focusedBlk = this.parent.currentFocusedBlock;
        const tableBlockElement = targetEl.closest(`.${constants.TABLE_BLOCK_CLS}`)
            || (focusedBlk && focusedBlk.closest(`.${constants.TABLE_BLOCK_CLS}`));
        if (!tableBlockElement) {
            return;
        }
        if (focusedBlk && this.parent.eventAction.isAnyPopupOpen()) {
            return;
        }
        const table = tableBlockElement.querySelector('table');
        const cell = targetEl.closest('td, th')
            || tableBlockElement.querySelector('td.e-cell-focus, th.e-cell-focus')
            || focusedBlk.closest('td, th');
        if (!cell) {
            return;
        }
        const blockId = tableBlockElement.id || tableBlockElement.getAttribute('data-block-id');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        if (!block) {
            return;
        }
        const settings = block.properties;
        const totalRows = settings.rows.length + (settings.enableHeader ? 1 : 0);
        const totalCols = settings.columns.length;
        const rowIdx = parseInt(cell.dataset.row || '-1', 10);
        const colIdx = parseInt(cell.dataset.col || '-1', 10);
        const tableGripperPopup = this.parent.rootEditorElement.querySelector('.e-table-gripper-action-popup');
        if (tableGripperPopup && tableGripperPopup.classList.contains('e-popup-open') && e.key === 'Escape') {
            this.parent.observer.notify('handleEscapeKey');
            return;
        }
        switch (e.key) {
            case 'Tab':
                e.preventDefault();
                this.moveFocus(table, rowIdx, colIdx, !e.shiftKey, block);
                break;
            case 'Escape':
                e.preventDefault();
                this.exitTableNavigation(table, 'forward');
                break;
            case 'Enter':
            case 'Backspace':
            case 'Delete': {
                if (!hasActiveTableSelection(tableBlockElement)) {
                    break;
                }
                e.preventDefault();
                e.stopPropagation();
                const targetCells = Array.from(table.querySelectorAll(`td.${constants.TABLE_CELL_FOCUS}, th.${constants.TABLE_CELL_FOCUS}`));
                // Gripper fallback if no rectangle focus
                if (targetCells.length === 0) {
                    // From selected rows
                    table.querySelectorAll('tr.e-row-selected').forEach((tr) => {
                        Array.from(tr.cells).forEach((cell) => {
                            if (!cell.classList.contains('e-row-number')) {
                                targetCells.push(cell);
                            }
                        });
                    });
                    // From selected columns
                    table.querySelectorAll('td.e-col-selected, th.e-col-selected').forEach((cell) => {
                        if (targetCells.indexOf(cell) === -1) {
                            targetCells.push(cell);
                        }
                    });
                }
                if (targetCells.length <= 1) {
                    break;
                }
                const uiManager = this.parent.blockRenderer.tableRenderer.getManager(blockId);
                const allEmpty = targetCells.every((cell) => (cell.textContent || '').trim() === '');
                if (!allEmpty) {
                    // 1st Delete → clear contents
                    this.parent.tableService.clearCellContents(table, targetCells);
                    // Refocus last cell
                    const lastCell = targetCells[targetCells.length - 1];
                    if (lastCell.tagName.toLowerCase() === 'td') {
                        this.parent.tableService.shiftFocusToBlockInCell(lastCell, true);
                    }
                    else {
                        setCursorPosition(lastCell, 0);
                    }
                    return;
                }
                this.parent.lastHighlightedBlockId = blockId;
                let shouldDeleteRows = false;
                let shouldDeleteCols = false;
                const rowIndicesToDelete = [];
                // Rectangle mode
                if (this.multiselectStartRow !== null &&
                    this.multiselectEndRow !== null &&
                    this.multiselectStartCol !== null &&
                    this.multiselectEndCol !== null) {
                    const minRow = Math.min(this.multiselectStartRow, this.multiselectEndRow);
                    const maxRow = Math.max(this.multiselectStartRow, this.multiselectEndRow);
                    const minCol = Math.min(this.multiselectStartCol, this.multiselectEndCol);
                    const maxCol = Math.max(this.multiselectStartCol, this.multiselectEndCol);
                    const isFullRow = minCol === 0 && maxCol >= totalCols - 1;
                    const isFullCol = minRow === 0 && maxRow >= totalRows - 1;
                    if (isFullRow) {
                        shouldDeleteRows = true;
                        for (let r = minRow; r <= maxRow; r++) {
                            rowIndicesToDelete.push(r);
                        }
                    }
                    if (isFullCol) {
                        shouldDeleteCols = true;
                    }
                }
                // Gripper mode
                const selectedRows = table.querySelectorAll('tr.e-row-selected');
                if (selectedRows.length > 0) {
                    shouldDeleteRows = true;
                    Array.from(selectedRows).forEach((tr) => {
                        const index = parseInt(tr.dataset.row || '-1', 10);
                        if (index >= 0 && rowIndicesToDelete.indexOf(index) === -1) {
                            rowIndicesToDelete.push(index);
                        }
                    });
                }
                const selectedColCells = table.querySelectorAll('td.e-col-selected, th.e-col-selected');
                if (selectedColCells.length > 0) {
                    const colCount = new Map();
                    Array.from(selectedColCells).forEach((cell) => {
                        const col = parseInt(cell.dataset.col || '-1', 10);
                        if (col >= 0) {
                            colCount.set(col, (colCount.get(col) || 0) + 1);
                        }
                    });
                    colCount.forEach((count) => {
                        if (count >= totalRows - (settings.enableHeader ? 1 : 0)) {
                            shouldDeleteCols = true;
                        }
                    });
                }
                // Execute deletion
                if (shouldDeleteCols && uiManager) {
                    uiManager.deleteSelectedColumns();
                    this.multiselectStartRow = this.multiselectEndRow =
                        this.multiselectStartCol = this.multiselectEndCol = null;
                }
                else if (shouldDeleteRows && uiManager) {
                    rowIndicesToDelete.sort((a, b) => b - a); // bottom to top
                    rowIndicesToDelete.forEach((rowIdx) => {
                        uiManager.deleteSelectedRows(rowIdx);
                    });
                    this.multiselectStartRow = this.multiselectEndRow =
                        this.multiselectStartCol = this.multiselectEndCol = null;
                }
                // Always clean UI
                uiManager.hideRowGripper();
                uiManager.hideAllPinnedColBars();
                uiManager.handleRemovePopup();
                break;
            }
            case 'ArrowUp':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight': {
                const direction = e.key.replace('Arrow', '').toLowerCase();
                if (e.shiftKey) {
                    if (!this.canAllowMultiSelection(cell, direction)) {
                        return;
                    }
                    this.parent.tableService.removeCellFocus(table);
                    if (this.multiselectStartRow == null || this.multiselectStartCol == null) {
                        this.multiselectStartRow = rowIdx;
                        this.multiselectStartCol = colIdx;
                    }
                    if (this.multiselectEndRow == null || this.multiselectEndCol == null) {
                        this.multiselectEndRow = rowIdx;
                        this.multiselectEndCol = colIdx;
                    }
                    e.preventDefault();
                    this.handleMultiselect(direction, table);
                    this.ensureCellVisible(table, getDataCell(table, this.multiselectEndRow, this.multiselectEndCol));
                    this.clearRangeAndSetCursor(table);
                }
                else {
                    // Clearing multi-select state
                    this.multiselectStartRow = null;
                    this.multiselectStartCol = null;
                    this.multiselectEndRow = null;
                    this.multiselectEndCol = null;
                    // Clear all row & column visual selections
                    const blockId = table.getAttribute('data-block-id');
                    const uiManager = this.parent.blockRenderer.tableRenderer.getManager(blockId);
                    if (uiManager) {
                        uiManager.removeRowColSelection(table);
                        uiManager.hideRowGripper(); // Hides row pinned bars
                        uiManager.hideAllPinnedColBars(); // Hides col pinned bars
                        uiManager.handleRemovePopup(); // Closes any popup if open
                    }
                    const selectedCells = getSelectedCells(tableBlockElement);
                    const cellName = cell.tagName.toLowerCase();
                    if (hasActiveTableSelection(tableBlockElement) && (selectedCells && selectedCells.length > 1)) {
                        if (cellName === 'td') {
                            this.parent.tableService.shiftFocusToBlockInCell(cell);
                        }
                        else {
                            // keep caret inside header cell (TH)
                            const th = cell;
                            // default position: keep current if selection is already inside th,
                            // else move caret to end (right/down) or start (left/up)
                            const moveToEnd = false;
                            setCursorPosition(th, moveToEnd ? (th.textContent || '').length : 0);
                        }
                    }
                    const isHorizontal = direction === 'left' || direction === 'right';
                    if (isHorizontal) {
                        const isHeader = cellName === 'th';
                        const shouldMoveHoriz = isHeader
                            ? this.shouldMoveHorizFromHeader(direction, cell)
                            : this.shouldMoveToAdjacentCell(direction, this.parent.currentFocusedBlock, block);
                        if (shouldMoveHoriz) {
                            e.preventDefault();
                            this.parent.tableService.removeCellFocus(table);
                            this.moveCellFocus(table, rowIdx, colIdx, direction);
                        }
                    }
                    else {
                        // Vertical navigation (up/down)
                        const isHeaderCell = cellName === 'th';
                        // From any header cell, ArrowUp exits the table backward
                        if (isHeaderCell && direction === 'up') {
                            e.preventDefault();
                            this.parent.tableService.removeCellFocus(table);
                            this.exitTableNavigation(table, 'backward');
                            return;
                        }
                        // Use the TH itself as the source when in header (no inner block exists)
                        // Use the currentFocusedBlock only for TD cells
                        const sourceForNav = isHeaderCell
                            ? cell
                            : this.parent.currentFocusedBlock;
                        const nextCellEl = this.shouldMoveCellFocus(direction, sourceForNav, table);
                        if (nextCellEl) {
                            e.preventDefault();
                            this.parent.tableService.removeCellFocus(table);
                            this.moveCellFocus(table, rowIdx, colIdx, direction);
                        }
                        else {
                            // Consider exiting the table if:
                            // - caret is at the correct boundary of the current focused block, AND
                            // - there is no adjacent block inside this cell in that direction, AND
                            // - we are at the first/last table row.
                            const settings = block.properties;
                            const totalRows = settings.rows.length + (settings.enableHeader ? 1 : 0);
                            const atBoundary = isHeaderCell
                                ? (direction === 'up'
                                    ? this.isCaretAtHeaderBoundary(cell, 'start')
                                    : this.isCaretAtHeaderBoundary(cell, 'end'))
                                : (direction === 'up'
                                    ? isAtStartOfBlock(this.parent.currentFocusedBlock)
                                    : isAtEndOfBlock(this.parent.currentFocusedBlock));
                            const isEdgeRow = direction === 'up'
                                ? rowIdx === 0
                                : rowIdx === (totalRows - 1);
                            // For headers, there is no inner adjacent block constraint
                            const hasAdjacentBlockInCell = isHeaderCell
                                ? false
                                : !!getAdjacentBlock(this.parent.currentFocusedBlock, direction === 'up' ? 'previous' : 'next');
                            if (atBoundary && isEdgeRow && !hasAdjacentBlockInCell) {
                                e.preventDefault();
                                this.exitTableNavigation(table, direction === 'up' ? 'backward' : 'forward');
                                return;
                            }
                        }
                    }
                }
                break;
            }
        }
    }
    clearRangeAndSetCursor(tableEle) {
        this.parent.nodeSelection.clearSelection();
        const lastSelectedCell = getDataCell(tableEle, this.multiselectEndRow, this.multiselectEndCol);
        if (lastSelectedCell) {
            setCursorPosition(lastSelectedCell, 0);
        }
    }
    shouldMoveCellFocus(direction, cellBlock, table) {
        const isUp = direction === 'up';
        const isDown = direction === 'down';
        const isLeft = direction === 'left';
        if (!cellBlock) {
            return null;
        }
        const hostCell = cellBlock.closest('td, th');
        if (!hostCell) {
            return null;
        }
        const isHeader = hostCell.tagName.toLowerCase() === 'th';
        if (!isHeader) {
            const adjacentBlock = getAdjacentBlock(cellBlock, (isUp || isLeft) ? 'previous' : 'next');
            if (adjacentBlock) {
                return null;
            }
        }
        if (isUp || isDown) {
            return getAdjacentCell(table, direction, hostCell);
        }
        return null;
    }
    // Move to adjacent cell only when,
    // * caret is at boundary AND there is no adjacent block inside the cell
    // * cell is empty
    shouldMoveToAdjacentCell(direction, cellBlock, blockModel) {
        const atStart = isAtStartOfBlock(cellBlock);
        const atEnd = isAtEndOfBlock(cellBlock);
        const isEmptyCell = cellBlock.textContent.trim() === '';
        if (isEmptyCell) {
            return true;
        }
        if (direction === 'left') {
            const prev = getAdjacentBlock(cellBlock, 'previous');
            const firstCellBlockId = blockModel.properties.rows[0].cells[0].blocks[0].id;
            return atStart && !prev && (cellBlock.id !== firstCellBlockId);
        }
        const next = getAdjacentBlock(cellBlock, 'next');
        return atEnd && !next;
    }
    // Helpers for keyboard shift selection behavior
    isSelectionWithinCell(cell) {
        const sel = this.parent.nodeSelection.getSelection();
        const anchorNode = sel.anchorNode;
        const focusNode = sel.focusNode;
        return cell.contains(anchorNode) && cell.contains(focusNode);
    }
    getCellBlockContentElements(cell) {
        const blocks = Array.from(cell.querySelectorAll('.e-block'));
        const contents = blocks
            .map((b) => getBlockContentElement(b))
            .filter((el) => !!el);
        return contents;
    }
    canAllowMultiSelection(cell, direction) {
        if (this.multiselectEndRow || this.multiselectEndCol) {
            return true;
        }
        const sel = window.getSelection && window.getSelection();
        if (!sel || sel.rangeCount === 0) {
            return false;
        }
        if (!this.isSelectionWithinCell(cell)) {
            return false;
        }
        const isLeft = direction === 'left';
        const isRight = direction === 'right';
        const isUpDown = direction === 'up' || direction === 'down';
        const range = sel.getRangeAt(0);
        if (cell.tagName === 'TH') {
            const startsAtFirst = range.startOffset === 0 && (isLeft || isUpDown);
            const endsAtLast = range.endOffset === cell.textContent.length && (isRight || isUpDown);
            return startsAtFirst || endsAtLast;
        }
        const contents = this.getCellBlockContentElements(cell);
        const isFirstTextNode = (!contents[0].firstChild ||
            (contents[0].firstChild && contents[0].firstChild.nodeType === Node.TEXT_NODE));
        const firstChild = isFirstTextNode
            ? contents[0]
            : contents[0].firstChild;
        const lastContent = contents[contents.length - 1];
        const isLastTextNode = (!lastContent.lastChild ||
            (lastContent.lastChild && lastContent.lastChild.nodeType === Node.TEXT_NODE));
        const lastChild = isLastTextNode
            ? lastContent
            : lastContent.lastChild;
        const expectedStartNode = getDeepestTextNode(firstChild) || contents[0];
        const expectedEndNode = getDeepestTextNode(lastChild) || lastContent;
        const startsAtFirst = (expectedStartNode && expectedStartNode.contains(range.startContainer) &&
            range.startOffset === 0 && (isLeft || isUpDown));
        const endsAtLast = (expectedEndNode && expectedEndNode.contains(range.endContainer) &&
            range.endOffset === lastChild.textContent.length && (isRight || isUpDown));
        return startsAtFirst || endsAtLast;
    }
    handleMultiselect(direction, table) {
        if (this.multiselectEndRow == null || this.multiselectEndCol == null) {
            return;
        }
        const blockId = table.getAttribute('data-block-id');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const settings = block.properties;
        const totalRows = (settings.rows).length + (settings.enableHeader ? 1 : 0);
        const totalCols = (settings.columns).length;
        let newEndRow = this.multiselectEndRow;
        let newEndCol = this.multiselectEndCol;
        switch (direction) {
            case 'up':
                newEndRow = Math.max(0, newEndRow - 1);
                break;
            case 'down':
                newEndRow = Math.min(totalRows - 1, newEndRow + 1);
                break;
            case 'left':
                newEndCol = Math.max(0, newEndCol - 1);
                break;
            case 'right':
                newEndCol = Math.min(totalCols - 1, newEndCol + 1);
                break;
        }
        this.updateRectangleFocus(this.multiselectStartRow, this.multiselectStartCol, newEndRow, newEndCol, table);
        this.multiselectEndRow = newEndRow;
        this.multiselectEndCol = newEndCol;
        // clear previous row/column selections
        const uiManager = this.parent.blockRenderer.tableRenderer.getManager(blockId);
        if (uiManager) {
            uiManager.removeRowColSelection(table);
            uiManager.hideRowGripper();
            uiManager.hideAllPinnedColBars();
        }
        const minRow = Math.min(this.multiselectStartRow, this.multiselectEndRow);
        const maxRow = Math.max(this.multiselectStartRow, this.multiselectEndRow);
        const minCol = Math.min(this.multiselectStartCol, this.multiselectEndCol);
        const maxCol = Math.max(this.multiselectStartCol, this.multiselectEndCol);
        // Re-check if full row selection (all columns selected)
        const isFullRowSelection = minCol === 0 && maxCol >= totalCols - 1;
        if (isFullRowSelection && uiManager) {
            let first = true;
            for (let r = minRow; r <= maxRow; r++) {
                uiManager.addRowSelection(table, r);
                uiManager.showRowGripperForDomRow(r, first);
                first = false;
            }
        }
        // Re-check if full column selection (all rows selected)
        const isFullColumnSelection = minRow === 0 && maxRow >= totalRows - 1;
        if (isFullColumnSelection && uiManager) {
            let first = true;
            for (let c = minCol; c <= maxCol; c++) {
                uiManager.addColumnSelection(table, c);
                uiManager.showColGripperForDomCol(c, first);
                first = false;
            }
        }
        if (!isFullRowSelection && !isFullColumnSelection) {
            uiManager.handleRemovePopup(); // Close popup when shrunk to single cell
        }
        this.ensureCellVisible(table, getDataCell(table, this.multiselectEndRow, this.multiselectEndCol));
        this.clearRangeAndSetCursor(table);
    }
    moveCellFocus(table, rowIdx, colIdx, direction) {
        const blockId = table.getAttribute('data-block-id');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const settings = block.properties;
        const totalRows = (settings.rows).length + (settings.enableHeader ? 1 : 0);
        const totalCols = (settings.columns).length;
        let targetRow = rowIdx;
        let targetCol = colIdx;
        switch (direction) {
            case 'up':
                if (rowIdx > 0) {
                    targetRow = rowIdx - 1;
                }
                break;
            case 'down':
                if (rowIdx + 1 < totalRows) {
                    targetRow = rowIdx + 1;
                }
                break;
            case 'left':
                if (colIdx > 0) {
                    targetCol = colIdx - 1;
                }
                else if (rowIdx > 0) {
                    targetRow = rowIdx - 1;
                    targetCol = totalCols - 1;
                }
                break;
            case 'right':
                if (colIdx + 1 < totalCols) {
                    targetCol = colIdx + 1;
                }
                else if (rowIdx + 1 < totalRows) {
                    targetRow = rowIdx + 1;
                    targetCol = 0;
                }
                break;
        }
        if (targetRow === rowIdx && targetCol === colIdx) {
            return;
        }
        const domCol = toDomCol(targetCol, settings.enableRowNumbers);
        const rowEl = table.rows[targetRow];
        const nextCell = rowEl && rowEl.cells[domCol];
        const cursorAtStart = direction === 'right' || direction === 'up' || direction === 'down';
        if (nextCell) {
            this.parent.tableService.removeCellFocus(table);
            this.parent.tableService.addCellFocus(nextCell, true, cursorAtStart);
            // Ensure focused cell is visible when navigating with arrow keys
            this.ensureCellVisible(table, nextCell);
            // If header cell, set caret directly in TH so cursor doesn’t remain in previous row’s TD
            if (nextCell.tagName && nextCell.tagName.toLowerCase() === 'th') {
                const th = nextCell;
                const placeAtEnd = direction === 'left' ? true : false; // moving left -> end, right -> start
                // For vertical moves, default to start
                requestAnimationFrame(() => {
                    setCursorPosition(th, placeAtEnd ? (th.textContent).length : 0);
                });
            }
        }
    }
    updateRectangleFocus(row1, col1, row2, col2, table) {
        const blockId = table.getAttribute('data-block-id');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const settings = block.properties;
        this.parent.tableService.removeCellFocus(table);
        const minRow = Math.min(row1, row2);
        const maxRow = Math.max(row1, row2);
        const minCol = Math.min(col1, col2);
        const maxCol = Math.max(col1, col2);
        for (let r = minRow; r <= maxRow; r++) {
            const tr = table.rows[r];
            for (let c = minCol; c <= maxCol; c++) {
                const domCol = toDomCol(c, settings.enableRowNumbers);
                const cellEl = tr.cells[domCol];
                if (cellEl) {
                    cellEl.classList.add(constants.TABLE_CELL_FOCUS);
                }
            }
        }
    }
    moveFocus(table, row, col, forward, block) {
        const settings = block.properties;
        this.parent.tableService.removeCellFocus(table);
        const headerOffset = settings.enableHeader ? 1 : 0;
        const totalRows = (settings.rows).length + headerOffset;
        const totalCols = (settings.columns).length;
        let nextRow = row;
        let nextCol = col;
        if (forward) {
            nextCol++;
            if (nextCol >= totalCols) {
                nextRow++;
            }
        }
        else {
            nextCol--;
            if (nextCol < 0) {
                nextRow--;
            }
        }
        // Forward past the last cell -> add a new row
        if (nextRow >= totalRows && nextCol >= totalCols) {
            // Add new row and focus its first cell
            this.parent.tableService.addRowAt({
                blockId: block.id,
                rowIndex: totalRows - headerOffset
            });
            const newLastRow = table.rows[table.rows.length - 1];
            this.parent.tableService.addCellFocus(newLastRow.cells[toDomCol(0, settings.enableRowNumbers)], true);
            return;
        }
        // Backward past the header (or top when no header) -> exit table
        // Note: only exit when we went before row 0 entirely.
        if (!forward && nextRow < 0 && nextCol < 0) {
            this.exitTableNavigation(table, 'backward');
            return;
        }
        // Clamp candidate row into [0, totalRows-1] to allow selecting header row as well
        const targetDOMRowIndex = Math.max(0, Math.min(nextRow, totalRows - 1));
        const rowElement = table.rows[targetDOMRowIndex];
        if (rowElement && nextCol >= 0 && nextCol < totalCols) {
            const domCol = toDomCol(nextCol, settings.enableRowNumbers);
            const targetCell = rowElement.cells[domCol];
            if (targetCell) {
                this.parent.tableService.addCellFocus(targetCell, true);
                this.ensureCellVisible(table, targetCell);
                if (targetCell.tagName.toLowerCase() === 'th') {
                    const th = targetCell;
                    // For Tab forward, place at start; for Shift+Tab, place at end
                    requestAnimationFrame(() => {
                        setCursorPosition(th, forward ? 0 : (th.textContent || '').length);
                    });
                }
                return;
            }
        }
        if (forward && targetDOMRowIndex < totalRows) {
            const domCol = toDomCol(0, settings.enableRowNumbers);
            const fallbackCell = table.rows[targetDOMRowIndex].cells[domCol];
            this.parent.tableService.addCellFocus(fallbackCell, true);
            this.ensureCellVisible(table, fallbackCell);
            if (fallbackCell && fallbackCell.tagName.toLowerCase() === 'th') {
                const th = fallbackCell;
                requestAnimationFrame(() => setCursorPosition(th, 0));
            }
        }
        else if (!forward && targetDOMRowIndex >= 0) {
            const domCol = toDomCol(totalCols - 1, settings.enableRowNumbers);
            const fallbackCell = table.rows[targetDOMRowIndex].cells[domCol];
            this.parent.tableService.addCellFocus(fallbackCell, true);
            this.ensureCellVisible(table, fallbackCell);
            if (fallbackCell && fallbackCell.tagName.toLowerCase() === 'th') {
                const th = fallbackCell;
                requestAnimationFrame(() => setCursorPosition(th, (th.textContent || '').length));
            }
        }
    }
    exitTableNavigation(table, direction) {
        const blockEl = table.closest('.e-block');
        const nextBlock = (direction === 'forward'
            ? blockEl.nextElementSibling : blockEl.previousElementSibling);
        if (nextBlock) {
            setCursorPosition(getBlockContentElement(nextBlock), 0);
            this.parent.setFocusToBlock(nextBlock);
            this.parent.tableService.removeCellFocus(table);
        }
    }
    destroy() {
        this.removeEventListener();
    }
}
